import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    TextField,
    CircularProgress,
} from "@mui/material";
import { AccountBalanceWalletOutlined as WithdrawIcon, WarningAmber as WarningIcon, InfoOutlined as InfoIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../config";
import { toast } from "react-toastify";

// This component now receives 'balance', 'phoneNumber', and 'onWithdrawalCompleted' as props
const WithdrawMoneyCard = ({ balance, phoneNumber, onWithdrawalCompleted }) => {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setAmount("");
    };

    const formatCurrency = (val) => {
        if (typeof val !== 'number' || isNaN(val)) return 'UGX 0';
        return `UGX ${parseFloat(val).toLocaleString("en-UG")}`;
    };

    const handleConfirmWithdraw = async () => {
        const withdrawAmount = parseFloat(amount);

        if (isNaN(withdrawAmount) || withdrawAmount < 10000) {
            toast.error("Minimum withdrawal is UGX 10,000.");
            return;
        }
        if (withdrawAmount > balance) {
            toast.error("Invalid amount or exceeds balance.");
            return;
        }

        setLoading(true);

        const percentageCharge = withdrawAmount * 0.0425;
        let systemCharge = 0;
        if (withdrawAmount < 200000) {
            systemCharge = 3000;
        } else if (withdrawAmount <= 999999) {
            systemCharge = 5000;
        } else {
            systemCharge = 10000;
        }
        const totalCharge = percentageCharge + systemCharge;
        const netAmount = withdrawAmount - totalCharge;

        if (netAmount <= 0) {
            toast.error("Insufficient funds after charges.");
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${baseURL}/user/withdraw`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: withdrawAmount,
                    system_charge: percentageCharge,
                    withdraw_charge: systemCharge,
                    net_amount: netAmount,
                    phone_number: phoneNumber,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                toast.success("Withdrawal request submitted successfully!");
                handleClose();
                // Call the prop function to refresh parent data
                if (onWithdrawalCompleted) {
                    onWithdrawalCompleted();
                }
            } else {
                toast.error(data.message || "Failed to process withdrawal. Ensure your account is verified.");
            }
        } catch {
            toast.error("Error processing withdrawal. Try again later.");
        }
        setLoading(false);
    };

    const calculateCharges = () => {
        const val = parseFloat(amount);
        if (isNaN(val) || val <= 0) return null;

        const percentageCharge = val * 0.0425;
        let systemCharge = 0;
        if (val < 200000) {
            systemCharge = 3000;
        } else if (val <= 999999) {
            systemCharge = 5000;
        } else {
            systemCharge = 10000;
        }

        const totalCharge = percentageCharge + systemCharge;
        const netAmount = val - totalCharge;

        if (netAmount <= 0 && val > 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#F8D7DA",
                        p: 2,
                        borderRadius: 2,
                        mb: 2,
                        mt: 2,
                        boxShadow: 0,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <WarningIcon sx={{ color: "#842029", mr: 1 }} />
                    <Typography variant="body2" sx={{ color: "#842029" }}>
                        Your balance is too low to cover withdrawal charges for this amount.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ mt: 3, p: 2, border: "1px solid #E0E0E0", borderRadius: 2, background: "#F9FAFB" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Platform Fee (4.25%):{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(percentageCharge)}
                    </Typography>
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Mobile Money Fee:{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(systemCharge)}
                    </Typography>
                </Typography>
                <Divider sx={{ my: 1, borderColor: "#eee" }} />
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#444" }}>
                    Total Charges:{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: "#222" }}>
                        {formatCurrency(totalCharge)}
                    </Typography>
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: COLORS.primary, mt: 1 }}>
                    Net Amount You Will Receive:{" "}
                    <Typography component="span" sx={{ fontWeight: "bold", color: COLORS.primary }}>
                        {formatCurrency(netAmount)}
                    </Typography>
                </Typography>
            </Box>
        );
    };

    return (
        <>
            <Card
                sx={{
                    p: 4,
                    mt: 4,
                    borderRadius: 2,
                    boxShadow: 0,
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: "#fff",
                    mx: "auto",
                }}
            >
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2.5, color: COLORS.primary }}>
                    Withdraw Your Funds
                </Typography>

                <Typography variant="body1" sx={{ color: "#555", mb: 1.5 }}>
                    Current Balance:{" "}
                    <Typography component="strong" sx={{ fontWeight: "bold", color: COLORS.primary }}>
                        {formatCurrency(balance)}
                    </Typography>
                </Typography>

                <Typography variant="body2" sx={{ color: "#777", mb: 3 }}>
                    Withdrawal requests are reviewed by our team. Please ensure your registered phone number is correct for a smooth transaction.
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<WithdrawIcon />}
                    onClick={handleOpen}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2.5,
                        background: COLORS.primary,
                        fontWeight: "bold",
                        fontSize: "1rem",
                        px: 3.5,
                        py: 1.5,
                        boxShadow: 'none',
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                            background: COLORS.primary,
                            boxShadow: "none",
                        },
                    }}
                >
                    Withdraw Funds
                </Button>
            </Card>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: "bold", pb: 1, fontSize: "1.3rem" }}>
                    Confirm Withdrawal Request
                </DialogTitle>
                <Divider sx={{ mx: 2 }} />
                <DialogContent sx={{ py: 3, px: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, color: "#333" }}>
                        Available Balance:{" "}
                        <Typography component="strong" sx={{ fontWeight: "bold", color: COLORS.primary }}>
                            {formatCurrency(balance)}
                        </Typography>
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            bgcolor: "#e6f7ff",
                            p: 2,
                            borderRadius: 2,
                            mb: 2,
                            border: "1px solid #91d5ff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                    >
                        <InfoIcon sx={{ color: "#1890ff", mr: 1 }} />
                        <Typography variant="body2" sx={{ color: "#2f54eb" }}>
                            Funds will be sent to your registered number:{" "}
                            <Typography component="strong" sx={{ fontWeight: "bold" }}>
                                {phoneNumber || "N/A"}
                            </Typography>
                            . To update this, please visit your{" "}
                            <Typography
                                component="span"
                                onClick={() => navigate("/user/account")}
                                sx={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    color: COLORS.primary,
                                    fontWeight: "bold",
                                    "&:hover": { opacity: 0.8 },
                                }}
                            >
                                profile settings
                            </Typography>
                            .
                        </Typography>
                    </Box>

                    <TextField
                        label="Amount to withdraw (UGX)"
                        fullWidth
                        variant="outlined"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        inputProps={{ min: 10000, max: balance }}
                        sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        helperText={
                            (amount && parseFloat(amount) > balance)
                                ? "Amount exceeds your available balance."
                                : (parseFloat(amount) > 0 && parseFloat(amount) < 10000)
                                    ? "Minimum withdrawal is UGX 10,000."
                                    : ""
                        }
                        error={
                            (amount && parseFloat(amount) > balance) ||
                            (parseFloat(amount) > 0 && parseFloat(amount) < 10000)
                        }
                    />

                    {amount && calculateCharges()}

                    <Typography variant="body2" sx={{ color: "#777", mt: 3, fontSize: "0.85rem" }}>
                        Note: Our team will review this withdrawal request and may contact you for confirmation before processing the funds. This process usually takes 1-2 business days.
                    </Typography>
                </DialogContent>
                <Divider sx={{ mx: 2 }} />
                <DialogActions sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        color="inherit"
                        disabled={loading}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            py: 1,
                            "&:hover": { borderColor: COLORS.primary, color: COLORS.primary },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmWithdraw}
                        variant="contained"
                        color="primary"
                        disabled={
                            loading ||
                            !amount ||
                            parseFloat(amount) <= 0 ||
                            parseFloat(amount) < 10000 ||
                            parseFloat(amount) > balance
                        }
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            py: 1,
                            ml: 1,
                            background: COLORS.primary,
                            "&:hover": { background: COLORS.primary, opacity: 0.9 },
                            "&:disabled": {
                                background: "#e0e0e0",
                                color: "#a0a0a0",
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Confirm Withdraw"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WithdrawMoneyCard;